import { useEffect, useMemo, useState } from "react";
import { IIndicators, PayedUpload } from "../../../../types/Indicators.type";
import { useToastContext } from "../../../../common/components/ui/toast/toast.context";
import { FormikProvider, useFormik } from "formik";
import InputFormik from "../../../../common/components/form/InputFormik";
import { maskCPF } from "../../../../common/helpers/mask.helpers";
import ButtonWithLoader from "../../../../common/components/ui/loading/ButtonLoading";
import { payedIndicator, updateIndicatorsProps } from "../../services/indicator.api";
import ComboBoxFormik from "../../../../common/components/form/ComboBoxFormik";
import * as Yup from 'yup';
import { isCPF } from "../../../../common/helpers/is-cpf";
import { IndicatorSendProofofPayment } from "../forms/fields/IndicatorSendProofofPayment";

type FormPropsPayedIndicator = {
  data: IIndicators
  formRequestErrors?: { message: string, path: string[] }[]
};

const validationSchema = Yup.object().shape({
  cpfFromIndicated: Yup.string()
    .required('CPF é obrigatório!')
    .test('valida-cpf', 'CPF inválido ou incompleto!', value => isCPF(value)),
  payed: Yup.string()
    .required('Selecione entre sim ou não.')
    .test(
      'is-valid-selection',
      'Por favor, selecione uma opção válida!',
      value => value !== "-"
    ),
});

export default function PayedIndicator(props: FormPropsPayedIndicator) {
  const { notifySuccess, notifyError } = useToastContext();
  const pathToUpload = useMemo(() => `indicators/${props.data._id}`, [props.data._id]);
  const [fileAttached, setFileAttached] = useState<File | null>(null);

  const formik = useFormik({
    initialValues: {
      cpfFromIndicated: "",
      payed: ""
    },
    enableReinitialize: true,
    validationSchema: validationSchema,
    onSubmit: async (data) => {
      const dataToSave = {
        _id: props.data._id,
        createAtPayed: new Date(),
        cpfFromIndicated: data.cpfFromIndicated,
        payed: data.payed,
      };
      const result = await payedIndicator(dataToSave);
      if (!result.ok) {
        notifyError(result.message as string);
        return;
      }
      notifySuccess("Registro realizado com sucesso!");
    },
  });

  const saveIndicatorUpload = async (payedUpload) => {
    const payedUploadArray = Array.isArray(payedUpload) ? payedUpload : [payedUpload];
    await updateIndicatorsProps(props.data._id, {
      payedUpload: [{ cpfFromIndicated: values.cpfFromIndicated, file: payedUploadArray[0], createAtPayed: new Date() }]
    })
    setFileAttached(payedUploadArray[0]);
  }

  const { values, isValid } = formik;

  const [, setCurrentDate] = useState(new Date().toISOString().split('T')[0]);
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentDate(new Date().toISOString().split('T')[0]);
    }, 60000); // Atualiza a cada minuto

    return () => clearInterval(interval); // Limpa o intervalo quando o componente é desmontado
  }, []);

  // Condição para habilitar o botão
  const isButtonDisabled = () => {
    if (values.payed === "yes") {
      return !fileAttached || !isValid;
    }
    return values.payed === "" || !isValid;
  };

  return (
    <form onSubmit={formik.handleSubmit}>
      <FormikProvider value={formik}>
        <section className="flex flex-col rounded-md text-black p-4 dark:text-black">
          <h2 className="text-2xl font-semibold mb-4">Confirmação de Pagamento</h2>
          <div className="flex flex-col gap-2">
            <InputFormik
              label="Digite o CPF do Indicado:"
              name="cpfFromIndicated"
              mask={maskCPF}
              required={true}
            />
            <ComboBoxFormik
              name="payed"
              label="Pagamento realizado:"
              options={[
                { value: "-", label: "-" },
                { value: "yes", label: "Sim" },
                { value: "no", label: "Não" }
              ]}
            />
          </div>
          {values.cpfFromIndicated !== "" && values.payed === 'yes' && (
            <div className="payment">
              <h2 className="mt-4 text-md font-semibold mb-4">Enviar Comprovante do Pagamento do Indicador</h2>
              <IndicatorSendProofofPayment
                onChange={saveIndicatorUpload}
                pathToUpload={pathToUpload}
                currentPath={props.data.payedUpload as PayedUpload[]}
              />
            </div>
          )}
          <div className="flex justify-end">
            <ButtonWithLoader
              isLoading={formik.isSubmitting}
              type="submit"
              disabled={isButtonDisabled()}
            >
              Enviar
            </ButtonWithLoader>
          </div>
        </section>
      </FormikProvider>
    </form>
  );
}
